import { useEffect, useMemo, useState } from 'react';
import careersHero1 from './assets/CareerHero_1.jpg';
import careersHero2 from './assets/CareerHero_2.jpeg';
import careersHero3 from './assets/CareerHero_3.jpg';
import careersImage1 from './assets/news/news3.jpg';
import careersImage2 from './assets/news/news4.jpg';
import careersImage3 from './assets/news/news7.jpg';

function CareersPage({ page }) {
  const heroSlides = [careersHero1, careersHero2, careersHero3];
  const [activeSlide, setActiveSlide] = useState(0);
  const [selectedPosition, setSelectedPosition] = useState(null);
  const [selectedOpportunity, setSelectedOpportunity] = useState(null);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveSlide((current) => (current + 1) % heroSlides.length);
    }, 6000);
    return () => clearInterval(intervalId);
  }, [heroSlides.length]);

  useEffect(() => {
    const animatedItems = document.querySelectorAll('.careers-animate');
    if (!animatedItems.length) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.2 }
    );

    animatedItems.forEach((item) => observer.observe(item));

    return () => observer.disconnect();
  }, []);

  const heroTitle = page?.title || 'Careers';
  const heroSummary =
    page?.summary ||
    'Join our mission to nurture learners with character, competence, and compassion.';

  const fallbackImages = useMemo(() => [careersImage1, careersImage2, careersImage3], []);
  const rawPhilosophy = useMemo(
    () =>
      page?.hiring_philosophy || [
        {
          title: 'Purpose-Driven Hiring',
          description:
            'We look for educators and staff who share our values, care for students, and commit to growth.',
          image: careersImage1,
        },
        {
          title: 'Professional Development',
          description:
            'Ongoing training, coaching, and collaborative planning that help every team member grow.',
          image: careersImage2,
        },
        {
          title: 'Supportive Culture',
          description:
            'A workplace rooted in respect, teamwork, and a shared commitment to student wellbeing.',
          image: careersImage3,
        },
      ],
    [page]
  );

  const hiringPhilosophy = useMemo(
    () =>
      rawPhilosophy.map((item, index) => {
        if (typeof item === 'string') {
          return {
            id: `career-${index + 1}`,
            title: item,
            description: 'Details will be updated soon.',
            image: fallbackImages[index % fallbackImages.length],
          };
        }

        const title = item?.title || `Career Focus ${index + 1}`;
        const normalizedId = String(item?.id || title)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');

        return {
          id: normalizedId || `career-${index + 1}`,
          title,
          description: item?.description || 'Details will be updated soon.',
          image: item?.image || fallbackImages[index % fallbackImages.length],
        };
      }),
    [rawPhilosophy, fallbackImages]
  );

  const defaultOpenPositions = [
    {
      id: 'primary-teacher',
      title: 'Primary Classroom Teacher',
      location: 'Puri Campus',
      type: 'Full-time',
      status: 'open',
      deadline: '2026-06-30',
      summary:
        'Lead engaging classroom instruction and support student growth in grades 1-6.',
      description: 'We are seeking an experienced and passionate Primary Classroom Teacher to join our dynamic team. You will create an engaging learning environment that fosters academic excellence and personal growth.',
      responsibilities: [
        'Plan and deliver daily lessons aligned with curriculum goals',
        'Create a supportive classroom environment that encourages participation',
        'Collaborate with grade-level teams and parents',
        'Assess student progress and provide constructive feedback',
        'Participate in professional development activities',
      ],
      requirements: [
        'Bachelor in Education or related field',
        '2+ years teaching experience preferred',
        'Strong classroom management and communication skills',
        'Proficiency in English and Indonesian',
        'Commitment to student-centered learning',
      ],
      applyNote: 'Email CV to hr@permataharapan.sch.id',
    },
    {
      id: 'ict-lab-assistant',
      title: 'ICT Lab Assistant',
      location: 'Simprug Campus',
      type: 'Full-time',
      status: 'open',
      deadline: '2026-05-31',
      summary:
        'Support digital learning, maintain lab equipment, and assist teachers during ICT sessions.',
      description: 'Join our IT team to support the school\'s digital transformation. You will maintain our ICT infrastructure and help students develop essential technology skills.',
      responsibilities: [
        'Maintain computers, projectors, and lab software',
        'Assist teachers with digital learning activities',
        'Provide basic tech support for students and staff',
        'Monitor lab equipment and report maintenance needs',
        'Conduct basic troubleshooting and system updates',
      ],
      requirements: [
        'Diploma/Bachelor in IT or related field',
        'Basic networking and troubleshooting skills',
        'Friendly, service-oriented communication',
        'Problem-solving mindset',
        'Willingness to learn new technologies',
      ],
      applyNote: 'Submit application via HR contact or school office',
    },
    {
      id: 'secondary-english-teacher',
      title: 'Secondary English Language Teacher',
      location: 'Puri Campus',
      type: 'Full-time',
      status: 'open',
      deadline: '2026-07-15',
      summary:
        'Teach English language and literature to grades 7-12, developing critical thinking and communication skills.',
      description: 'We are looking for a dynamic and innovative Secondary English Language Teacher to inspire students and develop their language proficiency. You will design engaging lessons that encourage literary appreciation and effective communication.',
      responsibilities: [
        'Deliver engaging English language and literature instruction',
        'Develop curriculum materials and assessments',
        'Facilitate discussions on diverse texts and contemporary issues',
        'Mentor students in debate, writing, and public speaking',
        'Collaborate with other subject teachers on integrated learning projects',
        'Prepare students for external examinations where applicable',
      ],
      requirements: [
        'Bachelor in English, Literature, or Education',
        '3+ years secondary teaching experience',
        'Excellent English communication and writing skills',
        'Experience with differentiated instruction',
        'Passion for developing student critical thinking and creativity',
        'Experience with digital learning tools preferred',
      ],
      applyNote: 'Send CV and cover letter to hr@permataharapan.sch.id',
    },
    {
      id: 'counselor-wellbeing',
      title: 'School Counselor / Wellbeing Coordinator',
      location: 'Simprug Campus',
      type: 'Full-time',
      status: 'open',
      deadline: '2026-08-31',
      summary:
        'Support student emotional and social wellbeing through counseling and wellness programs.',
      description: 'Join our wellbeing team to provide holistic support for our students. As a School Counselor, you will facilitate student development, provide individual and group counseling, and coordinate school-wide wellness initiatives.',
      responsibilities: [
        'Conduct individual and group counseling sessions',
        'Develop and implement school-wide wellness programs',
        'Collaborate with teachers and parents on student support plans',
        'Facilitate peer mediation and conflict resolution',
        'Maintain confidential student records and documentation',
        'Organize mental health awareness workshops and seminars',
        'Support student transitions and crisis intervention',
      ],
      requirements: [
        'Master\'s degree in Counseling, Psychology, or related field',
        'Licensed or eligible for licensure as a school counselor',
        '2+ years experience in educational or clinical counseling',
        'Strong interpersonal and communication skills',
        'Knowledge of adolescent development and trauma-informed practices',
        'Proficiency in English and Indonesian preferred',
      ],
      applyNote: 'Submit application with credentials to hr@permataharapan.sch.id',
    },
  ];

  const rawOpenPositions = page?.open_positions || defaultOpenPositions;
  const openPositions = useMemo(
    () =>
      rawOpenPositions.map((item, index) => {
        if (typeof item === 'string') {
          return {
            id: `position-${index + 1}`,
            title: item,
            location: 'Campus location',
            type: 'Full-time',
            status: 'open',
            deadline: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            summary: 'Job details will be updated soon.',
            description: 'Job details will be updated soon.',
            responsibilities: ['Role responsibilities will be updated soon.'],
            requirements: ['Role requirements will be updated soon.'],
            applyNote: 'Contact HR for application details.',
          };
        }

        const title = item?.title || `Position ${index + 1}`;
        const normalizedId = String(item?.id || title)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');

        return {
          id: normalizedId || `position-${index + 1}`,
          title,
          location: item?.location || 'Campus location',
          type: item?.type || 'Full-time',
          status: item?.status || 'open',
          deadline: item?.deadline || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          summary: item?.summary || 'Job details will be updated soon.',
          description: item?.description || 'Job details will be updated soon.',
          responsibilities: item?.responsibilities || ['Role responsibilities will be updated soon.'],
          requirements: item?.requirements || ['Role requirements will be updated soon.'],
          applyNote: item?.applyNote || 'Contact HR for application details.',
        };
      }),
    [rawOpenPositions]
  );

  const defaultOpportunities = [
    {
      id: 'student-teaching',
      title: 'Student Teaching Internship',
      description:
        'Gain hands-on teaching experience in a supportive environment with mentorship from experienced educators.',
      highlights: [
        'Classroom observation & co-teaching',
        'Lesson planning & curriculum design',
        'Student assessment & feedback',
      ],
      fullDescription: 'Our Student Teaching Internship program is designed for aspiring educators who want to gain practical classroom experience. You will work alongside experienced teachers, observe best practices, and gradually take on teaching responsibilities under close mentorship.',
      duration: '1-2 semesters',
      commitment: '20-30 hours per week',
      benefits: [
        'Hands-on classroom experience',
        'Mentorship from experienced educators',
        'Professional development workshops',
        'Certificate of completion',
        'Networking opportunities',
        'Potential for future employment',
      ],
      requirements: [
        'Currently enrolled in or recently completed education program',
        'Strong communication skills',
        'Passion for teaching and student development',
        'Reliability and commitment',
        'Background check required',
      ],
      contact: 'hr@permataharapan.sch.id',
    },
    {
      id: 'event-volunteer',
      title: 'Event & Program Volunteer',
      description:
        'Support school events, competitions, and community initiatives while building leadership and organizational skills.',
      highlights: [
        'Event coordination & logistics',
        'Student mentoring & support',
        'Community engagement activities',
      ],
      fullDescription: 'Join our dynamic events team and help create memorable experiences for our school community. From academic competitions to cultural celebrations, you\'ll play a key role in organizing and executing events that bring our community together.',
      duration: 'Flexible (per event basis)',
      commitment: '10-20 hours per month',
      benefits: [
        'Event planning experience',
        'Leadership development',
        'Community connection',
        'Volunteer certificate',
        'Social impact',
        'Team collaboration',
      ],
      requirements: [
        'Organizational skills',
        'Enthusiasm and positive attitude',
        'Ability to work in teams',
        'Flexibility with scheduling',
        'Attention to detail',
      ],
      contact: 'events@permataharapan.sch.id',
    },
    {
      id: 'research-volunteer',
      title: 'Research & Innovation Volunteer',
      description:
        'Contribute to educational research projects and help develop innovative learning solutions for our students.',
      highlights: [
        'Educational research support',
        'Technology & innovation projects',
        'Data analysis & reporting',
      ],
      fullDescription: 'Be part of our innovation lab where we explore new teaching methodologies and educational technologies. You\'ll contribute to research projects that shape the future of education at our school and potentially beyond.',
      duration: '3-6 months',
      commitment: '15-25 hours per week',
      benefits: [
        'Research experience',
        'Technology skills development',
        'Publication opportunities',
        'Academic recognition',
        'Innovation exposure',
        'Professional growth',
      ],
      requirements: [
        'Research or analytical background preferred',
        'Proficiency with data tools',
        'Critical thinking skills',
        'Attention to detail',
        'Ability to work independently',
      ],
      contact: 'research@permataharapan.sch.id',
    },
  ];

  const rawOpportunities = page?.internships_volunteering || defaultOpportunities;
  const opportunities = useMemo(
    () =>
      rawOpportunities.map((item, index) => {
        if (typeof item === 'string') {
          return {
            id: `opportunity-${index + 1}`,
            title: item,
            description: 'Opportunity details will be updated soon.',
            highlights: ['Detail 1', 'Detail 2', 'Detail 3'],
            fullDescription: 'Full details will be updated soon.',
            duration: 'Flexible',
            commitment: 'Varies',
            benefits: ['Benefit 1', 'Benefit 2', 'Benefit 3'],
            requirements: ['Requirement 1', 'Requirement 2', 'Requirement 3'],
            contact: 'hr@permataharapan.sch.id',
          };
        }

        const title = item?.title || `Opportunity ${index + 1}`;
        const normalizedId = String(item?.id || title)
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/(^-|-$)/g, '');

        return {
          id: normalizedId || `opportunity-${index + 1}`,
          title,
          description: item?.description || 'Opportunity details will be updated soon.',
          highlights: item?.highlights || ['Detail 1', 'Detail 2', 'Detail 3'],
          fullDescription: item?.fullDescription || 'Full details will be updated soon.',
          duration: item?.duration || 'Flexible',
          commitment: item?.commitment || 'Varies',
          benefits: item?.benefits || ['Benefit 1', 'Benefit 2', 'Benefit 3'],
          requirements: item?.requirements || ['Requirement 1', 'Requirement 2', 'Requirement 3'],
          contact: item?.contact || 'hr@permataharapan.sch.id',
        };
      }),
    [rawOpportunities]
  );

  // Helper function to calculate days until deadline
  const getDaysUntilDeadline = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Helper function to get status badge
  const getStatusBadge = (status, deadline) => {
    const daysLeft = getDaysUntilDeadline(deadline);
    if (status === 'closed' || daysLeft < 0) {
      return { text: 'Closed', color: '#ef4444' };
    }
    if (daysLeft <= 7) {
      return { text: `${daysLeft} days left`, color: '#f97316' };
    }
    return { text: 'Open Now', color: '#7c3aed' };
  };

  // Filter only open positions
  const activePositions = openPositions.filter(pos => {
    const daysLeft = getDaysUntilDeadline(pos.deadline);
    return pos.status === 'open' && daysLeft >= 0;
  });

  return (
    <section className="careers-page">
      <section className="admissions-hero" id="careers-overview">
        <div className="admissions-hero-media" aria-hidden="true">
          {heroSlides.map((slide, index) => (
            <div
              key={slide}
              className={`admissions-hero-slide ${index === activeSlide ? 'active' : ''}`}
            >
              <img src={slide} alt="Careers hero" />
            </div>
          ))}
        </div>
        <div className="admissions-hero-overlay" aria-hidden="true" />
        <div className="admissions-hero-content">
          <p className="admissions-hero-kicker">Careers</p>
          <h2>{heroTitle}</h2>
          <p>{heroSummary}</p>
          <div className="admissions-hero-dots" role="tablist" aria-label="Careers hero slides">
            {heroSlides.map((slide, index) => (
              <button
                key={slide}
                type="button"
                className={`admissions-hero-dot ${index === activeSlide ? 'active' : ''}`}
                onClick={() => setActiveSlide(index)}
                aria-label={`Show slide ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="careers-join" id="join-our-team">
        <div className="careers-join-wrapper">
          {/* Header Section */}
          <div className="careers-join-header careers-animate">
            <p className="careers-join-kicker">Join Our Team</p>
            <h2>Philosophy of Hiring and Development</h2>
            <p>
              We invest in educators and staff who share our mission, grow with our community, and are committed to nurturing the next generation of learners.
            </p>
          </div>

          {/* Philosophy Cards Grid */}
          <div className="careers-philosophy-grid careers-animate">
            {hiringPhilosophy.map((item) => (
              <article key={item.id} id={item.id} className="philosophy-card">
                <div className="philosophy-icon">
                  <i className={`fas ${item.id.includes('purpose') ? 'fa-heart' : item.id.includes('professional') ? 'fa-graduation-cap' : 'fa-handshake'}`} />
                </div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
                <div className="philosophy-highlight">
                  <div className="philosophy-highlight-icon">
                    <i className="fas fa-star" />
                  </div>
                  <div className="philosophy-highlight-text">
                    <strong>Key Focus</strong>
                    <span>
                      {item.id.includes('purpose') && 'Values-aligned hiring'}
                      {item.id.includes('professional') && 'Continuous growth'}
                      {item.id.includes('supportive') && 'Team collaboration'}
                    </span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* CTA Section */}
          <div className="careers-join-cta careers-animate">
            <div className="careers-join-cta-content">
              <h3>Ready to Make a Difference?</h3>
              <p>
                Join our dedicated team of educators and professionals committed to excellence in education and student development.
              </p>
              <a href="#open-positions" className="careers-join-cta-button">
                <i className="fas fa-arrow-right" />
                Explore Open Positions
              </a>
            </div>
          </div>
        </div>
      </section>

      <section className="careers-open" id="open-positions">
        <div className="careers-open-wrapper">
          {/* Header Section */}
          <div className="careers-open-header careers-animate">
            <p className="careers-open-kicker">Open Positions</p>
            <h2>Join Our Growing Team</h2>
            <p>
              Explore current opportunities and discover how you can make an impact in our school community. Apply today to become part of our mission.
            </p>
          </div>

          {/* Open Positions Preview Grid */}
          {activePositions.length > 0 ? (
            <div className="careers-open-list careers-animate">
              {activePositions.map((role) => {
                const statusInfo = getStatusBadge(role.status, role.deadline);
                
                return (
                  <article
                    key={role.id}
                    id={role.id}
                    className="careers-open-card"
                    onClick={() => setSelectedPosition(role)}
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setSelectedPosition(role);
                      }
                    }}
                  >
                    {/* Card Header */}
                    <div className="careers-open-card-header">
                      <div className="careers-open-card-title-block">
                        <h3 className="careers-open-card-title">{role.title}</h3>
                        <p className="careers-open-card-summary">{role.summary}</p>
                      </div>
                      <div className="careers-open-card-meta">
                        <div className="careers-open-meta-badge" style={{ background: `linear-gradient(135deg, ${statusInfo.color}, ${statusInfo.color}cc)` }}>
                          <i className="fas fa-circle" style={{ fontSize: '0.5rem' }} />
                          {statusInfo.text}
                        </div>
                        <div className="careers-open-meta-info">
                          <div className="careers-open-meta-item">
                            <i className="fas fa-map-marker-alt" />
                            {role.location}
                          </div>
                          <div className="careers-open-meta-item">
                            <i className="fas fa-briefcase" />
                            {role.type}
                          </div>
                          <div className="careers-open-meta-item">
                            <i className="fas fa-calendar" />
                            Apply by {new Date(role.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Card Preview */}
                    <div className="careers-open-card-preview">
                      <p>{role.description}</p>
                      <div className="careers-open-card-cta">
                        <span className="careers-open-view-details">View Details</span>
                        <i className="fas fa-arrow-right" />
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          ) : (
            <div className="careers-open-empty">
              <i className="fas fa-briefcase" />
              <h3>No Open Positions</h3>
              <p>There are currently no open positions. Please check back soon!</p>
            </div>
          )}
        </div>
      </section>

      {/* Position Detail Modal */}
      {selectedPosition && (
        <div className="careers-position-modal-overlay" onClick={() => setSelectedPosition(null)}>
          <div className="careers-position-modal" onClick={(e) => e.stopPropagation()}>
            <button className="careers-modal-close" onClick={() => setSelectedPosition(null)}>
              <i className="fas fa-times" />
            </button>

            <div className="careers-modal-header">
              <div>
                <h2>{selectedPosition.title}</h2>
                <p className="careers-modal-summary">{selectedPosition.summary}</p>
              </div>
              <div className="careers-modal-meta">
                <div className="careers-modal-meta-item">
                  <i className="fas fa-map-marker-alt" />
                  <span>{selectedPosition.location}</span>
                </div>
                <div className="careers-modal-meta-item">
                  <i className="fas fa-briefcase" />
                  <span>{selectedPosition.type}</span>
                </div>
                <div className="careers-modal-meta-item">
                  <i className="fas fa-calendar" />
                  <span>Apply by {new Date(selectedPosition.deadline).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                </div>
              </div>
            </div>

            <div className="careers-modal-body">
              <section className="careers-modal-section">
                <h3>About This Role</h3>
                <p>{selectedPosition.description}</p>
              </section>

              <section className="careers-modal-section">
                <h3>Responsibilities</h3>
                <ul className="careers-modal-list">
                  {selectedPosition.responsibilities.map((item) => (
                    <li key={item}>
                      <i className="fas fa-check-circle" />
                      {item}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="careers-modal-section">
                <h3>Requirements</h3>
                <ul className="careers-modal-list">
                  {selectedPosition.requirements.map((item) => (
                    <li key={item}>
                      <i className="fas fa-star" />
                      {item}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="careers-modal-section">
                <h3>How to Apply</h3>
                <div className="careers-modal-apply">
                  <i className="fas fa-envelope" />
                  <p>{selectedPosition.applyNote}</p>
                </div>
              </section>
            </div>

            <div className="careers-modal-footer">
              <button className="careers-modal-apply-btn" onClick={() => window.location.href = `mailto:hr@permataharapan.sch.id?subject=Application for ${selectedPosition.title}`}>
                <i className="fas fa-paper-plane" />
                Apply Now
              </button>
              <button className="careers-modal-close-btn" onClick={() => setSelectedPosition(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <section className="careers-opportunities" id="internships-volunteering">
        <div className="careers-opportunities-wrapper">
          {/* Header Section */}
          <div className="careers-opportunities-header careers-animate">
            <p className="careers-opportunities-kicker">Internships / Volunteering</p>
            <h2>Opportunities to Learn, Serve, and Grow</h2>
            <p>
              Short-term roles that support the school community while building valuable experience and making a meaningful impact.
            </p>
          </div>

          {/* Opportunities Grid */}
          <div className="careers-opportunities-grid careers-animate">
            {opportunities.map((item) => (
              <article 
                key={item.id} 
                id={item.id} 
                className="careers-opportunity-card"
                onClick={() => setSelectedOpportunity(item)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setSelectedOpportunity(item);
                  }
                }}
              >
                {/* Icon */}
                <div className="careers-opportunity-icon">
                  <i className={`fas ${item.id.includes('teaching') ? 'fa-chalkboard-user' : item.id.includes('event') ? 'fa-calendar-check' : 'fa-flask'}`} />
                </div>

                {/* Body */}
                <div className="careers-opportunity-body">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </div>

                {/* Highlights */}
                <div className="careers-opportunity-highlights">
                  {item.highlights.map((highlight, idx) => (
                    <div key={idx} className="opportunity-highlight">
                      <i className="fas fa-check-circle" />
                      <span>{highlight}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <button className="careers-opportunity-cta">
                  <span>Learn More</span>
                  <i className="fas fa-arrow-right" />
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunity Detail Modal */}
      {selectedOpportunity && (
        <div className="careers-opportunity-modal-overlay" onClick={() => setSelectedOpportunity(null)}>
          <div className="careers-opportunity-modal" onClick={(e) => e.stopPropagation()}>
            <button className="careers-opportunity-modal-close" onClick={() => setSelectedOpportunity(null)}>
              <i className="fas fa-times" />
            </button>

            <div className="careers-opportunity-modal-header">
              <div className="opportunity-modal-icon">
                <i className={`fas ${selectedOpportunity.id.includes('teaching') ? 'fa-chalkboard-user' : selectedOpportunity.id.includes('event') ? 'fa-calendar-check' : 'fa-flask'}`} />
              </div>
              <div>
                <h2>{selectedOpportunity.title}</h2>
                <p className="opportunity-modal-summary">{selectedOpportunity.description}</p>
              </div>
            </div>

            <div className="careers-opportunity-modal-meta">
              <div className="opportunity-modal-meta-item">
                <i className="fas fa-hourglass-half" />
                <div>
                  <strong>Duration</strong>
                  <span>{selectedOpportunity.duration}</span>
                </div>
              </div>
              <div className="opportunity-modal-meta-item">
                <i className="fas fa-clock" />
                <div>
                  <strong>Time Commitment</strong>
                  <span>{selectedOpportunity.commitment}</span>
                </div>
              </div>
            </div>

            <div className="careers-opportunity-modal-body">
              <section className="opportunity-modal-section">
                <h3>About This Opportunity</h3>
                <p>{selectedOpportunity.fullDescription}</p>
              </section>

              <section className="opportunity-modal-section">
                <h3>What You'll Gain</h3>
                <ul className="opportunity-modal-list">
                  {selectedOpportunity.benefits.map((benefit) => (
                    <li key={benefit}>
                      <i className="fas fa-star" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="opportunity-modal-section">
                <h3>What We're Looking For</h3>
                <ul className="opportunity-modal-list">
                  {selectedOpportunity.requirements.map((requirement) => (
                    <li key={requirement}>
                      <i className="fas fa-check-circle" />
                      {requirement}
                    </li>
                  ))}
                </ul>
              </section>
            </div>

            <div className="careers-opportunity-modal-footer">
              <button 
                className="opportunity-modal-apply-btn" 
                onClick={() => window.location.href = `mailto:${selectedOpportunity.contact}?subject=Interest in ${selectedOpportunity.title}`}
              >
                <i className="fas fa-paper-plane" />
                Get Started
              </button>
              <button className="opportunity-modal-close-btn" onClick={() => setSelectedOpportunity(null)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

export default CareersPage;
